import { FC, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Avatar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
} from '@mui/material';
import { UserByCategoryItem } from '../../../apis/users/users.interfaces';

const getInitials = (first: string, last: string) =>
  `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();

const getFullName = (first: string, middle: string | undefined, last: string) =>
  [first, middle, last].filter(Boolean).join(' ');

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const STATUS_COLORS: Record<string, string> = {
  active: '#2e7d32',
  inactive: '#9e9e9e',
  pending: '#ed6c02',
  blocked: '#d32f2f',
};

interface CategoryUsersAdminViewProps {
  users: UserByCategoryItem[];
  isLoading?: boolean;
  total: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const CategoryUsersAdminView: FC<CategoryUsersAdminViewProps> = ({
  users,
  isLoading,
  total,
  page,
  limit,
  onPageChange,
  onLimitChange,
}) => {
  const rows = useMemo(() => users, [users]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4} data-testid="category-users-admin-loading">
        <CircularProgress />
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box display="flex" justifyContent="center" mt={4} data-testid="category-users-admin-empty">
        <Typography variant="body2" color="text.secondary">No users found for this category.</Typography>
      </Box>
    );
  }

  return (
    <Paper variant="outlined" data-testid="category-users-admin-table">
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((user) => (
              <TableRow key={user.id} hover data-testid={`user-row-${user.id}`}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar
                      src={user.profilePicture}
                      sx={{ width: 28, height: 28, fontSize: 11, bgcolor: 'primary.main' }}
                    >
                      {getInitials(user.firstName, user.lastName)}
                    </Avatar>
                    <Typography variant="body2">
                      {getFullName(user.firstName, user.middleName, user.lastName)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={user.userRole} size="small" variant="outlined" color="primary" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{user.email ?? '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{user.phone ?? '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.userStatus}
                    size="small"
                    sx={{ bgcolor: STATUS_COLORS[user.userStatus] ?? '#757575', color: '#fff' }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {user.createdAt ? formatDate(user.createdAt) : '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
        rowsPerPage={limit}
        onRowsPerPageChange={(e) => {
          onLimitChange(parseInt(e.target.value, 10));
          onPageChange(1);
        }}
        rowsPerPageOptions={[10, 25, 50]}
        data-testid="category-users-admin-pagination"
      />
    </Paper>
  );
};

export default CategoryUsersAdminView;
